import { RESUME_TOKEN_QUERY_KEY } from '~/assets/constants';
import { getResumeForm } from '~/helpers/http';
import { getCachedResumeToken } from '~/helpers/local-storage';
import type { CountryKey, ResumeStatus } from '~/types';

type Mechanism = 'query' | 'cache' | null;

export default function useFormResume(country: CountryKey) {
  const { getQueryParam, clearQueryParam } = useRouting();
  const token = useState(() => '');
  const mechanism = useState<Mechanism>(() => null);
  const status = useState((): ResumeStatus => 'pending');

  watch(token, token => {
    status.value = token ? 'resumable' : 'non_resumable';
  });

  onMounted(() => {
    if (status.value != 'pending') return;
    const queryToken = getQueryParam(RESUME_TOKEN_QUERY_KEY);
    if (queryToken) {
      token.value = queryToken;
      mechanism.value = 'query';
    }
    else {
      trySetCachedToken();
    }
    clearQueryParam(RESUME_TOKEN_QUERY_KEY);
  });

  return {
    resumeStatus: status,
    resumeMechanism: mechanism,

    async resume() {
      if (!token.value) throw new Error('No token to resume form');
      status.value = 'resuming';
      const form = await getResumeForm(token.value).catch(() => null);
      status.value = form ? 'success' : 'error';
      if (!form) throw new Error('Failed to resume form');
      return form;
    }
  };

  function trySetCachedToken() {
    const cachedToken = getCachedResumeToken(country);
    if (cachedToken) {
      token.value = cachedToken;
      mechanism.value = 'cache';
    }
  }
}